import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

interface PreviewFileProps {
  open: boolean;
  title: string;
  imagePath: string;
  onCloseClicked: () => void;
}

const getFileType = (filePath: string) => {
  const normalizedPath = filePath?.split("?")[0]?.split("#")[0]?.toLowerCase();
  if (!normalizedPath) {
    return "unknown";
  }

  if (normalizedPath.endsWith(".pdf")) {
    return "pdf";
  }

  if (
    normalizedPath.endsWith(".png") ||
    normalizedPath.endsWith(".jpg") ||
    normalizedPath.endsWith(".jpeg") ||
    normalizedPath.endsWith(".gif") ||
    normalizedPath.endsWith(".bmp") ||
    normalizedPath.endsWith(".webp") ||
    normalizedPath.endsWith(".svg")
  ) {
    return "image";
  }

  return "unknown";
};

const PreviewFile = ({
  open = false,
  title = "",
  imagePath = "",
  onCloseClicked,
}: PreviewFileProps) => {
  const openedPdfRef = useRef("");
  const fileType = useMemo(() => getFileType(imagePath), [imagePath]);
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) {
      openedPdfRef.current = "";
      return;
    }

    if (fileType !== "pdf" || !imagePath) {
      return;
    }

    if (openedPdfRef.current === imagePath) {
      return;
    }

    openedPdfRef.current = imagePath;
    window.open(imagePath, "_blank", "noopener,noreferrer");
    onCloseClicked();
  }, [fileType, imagePath, onCloseClicked, open]);

  return (
    <Dialog
      open={open && fileType !== "pdf"}
      maxWidth="xl"
      onClose={() => onCloseClicked()}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {fileType === "image" && imagePath && (
          <img
            src={imagePath}
            alt={title}
            style={{ maxWidth: "100%", maxHeight: "75vh" }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onCloseClicked()}>{t("close")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreviewFile;
